import { useMutation } from '@apollo/client'
import { Box, Text } from '@chakra-ui/react'
import { isArray } from 'lodash-es'
import { parseCookies } from 'nookies'
import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import Button from '_app/components/Buttons/Button'
import { CloseIcon } from '_app/components/Icons'
import Modal from '_app/components/Modal'
import { CustomerAddress } from '_app/graphql/generated/graphql'
import { SET_BILLING_ADDRESS_ON_CART } from '_app/graphql/mutations/cart/SetBillingAddressOnCart'
import { SET_SHIPPING_ADDRESSES_ON_CART } from '_app/graphql/mutations/cart/SetShippingAddressOnCart'
import useCustomer from '_app/hooks/useCustomer'
import { STEPS } from 'modules/checkout/types'
import Addresses from './tabs/Addresses'

interface AddressesModalProps {
  onClose(): void;
  setStep: (step: number) => void;
}

const AddressesModal = ({ onClose, setStep }: AddressesModalProps) => {
  const { t } = useTranslation('profile')

  const { customer } = useCustomer()

  const [selectedAddress, setSelectedAddress] =
    useState<CustomerAddress | null>(null)

  const [setBillingAddressOnCart] = useMutation(SET_BILLING_ADDRESS_ON_CART)

  const [setShippingAddressesOnCart] = useMutation(
    SET_SHIPPING_ADDRESSES_ON_CART
  )

  const onSubmit = async () => {
    if (!selectedAddress) {
      return null
    }
    await setShippingAddressesOnCart({
      variables: {
        cartId: parseCookies().cartId,
        shippingAddresses: [
          {
            customer_address_id: selectedAddress.id,
          },
        ],
      },
    })

    await setBillingAddressOnCart({
      variables: {
        billingAddress: {
          customer_address_id: selectedAddress.id,
        },
      },
      refetchQueries: ['GetCart'],
    })

    setStep(STEPS.PAYMENT)
    onClose()
  }

  return (
    <Modal
      isOpen
      height="100%"
      maxWidth={750}
      maxH={{ base: '100vh', md: '750px' }}
      onClose={onClose}
      p={{ base: '16px', md: '30px' }}
    >
      <CloseIcon
        position="absolute"
        top={{ base: '16px', md: '30px' }}
        right={{ base: '16px', md: '30px' }}
        cursor="pointer"
        onClick={() => onClose()}
      />
      <Text mb="24px">{t('SAVED_ADDRESSES')}</Text>
      <Box
        maxHeight={{ base: 'calc(100vh - 150px)', md: '500px' }}
        overflowY="scroll"
        overflowX="hidden"
      >
        {isArray(customer?.addresses) && (
          <Addresses
            addresses={(customer?.addresses as CustomerAddress[]).map(a => ({
              ...a,
              isSelected: a.id === selectedAddress?.id,
            }))}
            onClick={setSelectedAddress}
            cols={2}
          />
        )}
      </Box>
      <Button
        width="full"
        mt={{ base: '16px', md: '30px' }}
        onClick={onSubmit}
        isDisabled={!selectedAddress}
      >
        {t('CHOOSE', { ns: 'common' })}
      </Button>
    </Modal>
  )
}

export default AddressesModal
